/**
 * App-wide state for the customer site: public configuration, the signed-in user (session),
 * CSRF token and wallet balances. Areas (core, casino, sports) consume it via useAppState().
 *
 * Also here: per-device display preferences (odds format, hidden balance) and the shell API
 * (open the login / sign-up sheet or the account menu from any area).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import type { Balances, PublicConfig, SessionResponse, SessionUser } from "../shared/api.ts";
import { api, setCsrfToken } from "../ui/api.ts";
import { PageLoader } from "../ui/components.tsx";
import type { OddsFormat } from "../ui/format.ts";
import { isBookiesHost } from "../shared/bookies.ts";

interface AppState {
  shopMode: boolean;
  shopSessionClosed: string | null;
  clearShopSessionClosed: () => void;
  config: PublicConfig | null;
  configError: string | null;
  user: SessionUser | null;
  sessionLoaded: boolean;
  balances: Balances | null;
  refreshSession: () => Promise<SessionUser | null>;
  refreshBalances: () => Promise<void>;
  setBalances: (b: Balances) => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AppState | null>(null);

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [balances, setBalances] = useState<Balances | null>(null);
  const [shopMode, setShopMode] = useState(() => { try { return new URLSearchParams(location.search).get('shop') === '1' || sessionStorage.getItem('wb24.shop.mode') === '1'; } catch { return false; } });
  const [shopSessionClosed, setShopSessionClosed] = useState<string | null>(null);
  const rememberShopMode = useCallback((value: boolean) => {
    setShopMode(value);
    try { if (value) sessionStorage.setItem('wb24.shop.mode', '1'); else sessionStorage.removeItem('wb24.shop.mode'); } catch { /* Storage is optional. */ }
  }, []);
  const closeShopSession = useCallback((message: string) => {
    setShopSessionClosed(message); rememberShopMode(true); setUser(null); setBalances(null); setCsrfToken(null);
  }, [rememberShopMode]);
  const clearShopSessionClosed = useCallback(() => setShopSessionClosed(null), []);
  useEffect(() => {
    const onClose = (event: Event) => closeShopSession(String((event as CustomEvent).detail ?? 'Your play session has ended. Ask the cashier for credit.'));
    window.addEventListener('wb24:shop-session-closed', onClose);
    return () => window.removeEventListener('wb24:shop-session-closed', onClose);
  }, [closeShopSession]);

  const refreshConfig = useCallback(async () => {
    try {
      setConfig(await api.get<PublicConfig>("/api/public/config"));
      setConfigError(null);
    } catch (e) {
      setConfigError(e instanceof Error ? e.message : "Unable to load configuration");
    }
  }, []);

  const refreshBalances = useCallback(async () => {
    if (isBookiesHost(location.hostname)) return;
    try {
      const r = await api.get<{ balances: Balances }>("/api/wallet");
      setBalances(r.balances);
    } catch {
      setBalances(null);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const s = await api.get<SessionResponse>("/api/auth/session");
    setCsrfToken(s.csrfToken);
    setUser(s.user);
    setSessionLoaded(true);
    if (s.shopSessionClosed) closeShopSession(s.shopSessionClosed);
    else if (s.user) { setShopSessionClosed(null); rememberShopMode(!!s.user.shopPlayer || !!s.user.shopOwnerId); }
    if (s.user) void refreshBalances();
    else setBalances(null);
    await refreshConfig();
    return s.user;
  }, [refreshBalances, refreshConfig, closeShopSession, rememberShopMode]);

  useEffect(() => {
    if (!user?.shopPlayer) return;
    const poll = window.setInterval(() => {
      void api.get<SessionResponse>('/api/auth/session').then(session => {
        if (session.shopSessionClosed) closeShopSession(session.shopSessionClosed);
        else { setUser(session.user); setCsrfToken(session.csrfToken); if (!session.user) setBalances(null); }
      }).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(poll);
  }, [user?.id, user?.shopPlayer, closeShopSession]);
  useEffect(() => {
    if (!user?.shopPlayer || !balances || balances.reserved > 0 || balances.available >= (user.shopMinStake ?? 100)) return;
    const timer = window.setTimeout(() => { void refreshSession().catch(() => undefined); }, 1000);
    return () => window.clearTimeout(timer);
  }, [user?.shopPlayer, user?.shopMinStake, balances, refreshSession]);

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      setCsrfToken(null);
      setUser(null);
      setBalances(null);
      await refreshSession().catch(() => undefined);
    }
  }, [refreshSession]);

  useEffect(() => {
    refreshSession().catch(() => {
      setSessionLoaded(true);
      void refreshConfig();
    });
  }, [refreshSession, refreshConfig]);

  const value = useMemo<AppState>(
    () => ({ config, configError, user, sessionLoaded, balances, refreshSession, refreshBalances, setBalances, signOut, shopMode, shopSessionClosed, clearShopSessionClosed }),
    [config, configError, user, sessionLoaded, balances, refreshSession, refreshBalances, signOut, shopMode, shopSessionClosed, clearShopSessionClosed],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppState(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAppState outside AppStateProvider");
  return v;
}

/** Payment pages use cash balances without replacing the active gameplay balance. */
export function useWalletBalances(cash = false) {
  const { user, balances, refreshBalances } = useAppState();
  const separate = cash && !!user?.ownerTestMode;
  const [cashBalances, setCashBalances] = useState<{ userId: string; balances: Balances } | null>(null);
  const refresh = useCallback(async () => {
    if (!separate) return refreshBalances();
    try {
      const response = await api.get<{ balances: Balances }>("/api/wallet/cash");
      if (user) setCashBalances({ userId: user.id, balances: response.balances });
    } catch {
      setCashBalances(null);
    }
  }, [separate, user?.id, refreshBalances]);
  useEffect(() => { if (separate) void refresh(); }, [separate, refresh]);
  return { balances: separate ? (cashBalances?.userId === user?.id ? cashBalances?.balances ?? null : null) : balances, refreshBalances: refresh, label: separate ? "Cash balance" : "Balance" };
}

/** Wraps pages that need a signed-in user; sends visitors to /login?next=… */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, sessionLoaded } = useAppState();
  const location = useLocation();
  if (!sessionLoaded) return <PageLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

// ---------------------------------------------------------------- device preferences

/** A string preference in localStorage that every subscriber sees change (also across tabs). */
function localPreference<T extends string>(key: string, parse: (raw: string | null) => T) {
  const listeners = new Set<() => void>();
  let cached: T | null = null;
  const read = (): T => {
    if (cached === null) {
      let raw: string | null = null;
      try {
        raw = localStorage.getItem(key);
      } catch {
        raw = null;
      }
      cached = parse(raw);
    }
    return cached;
  };
  const write = (value: T) => {
    cached = value;
    try {
      localStorage.setItem(key, value);
    } catch {
      /* storage unavailable: keep the value for this page view */
    }
    for (const l of [...listeners]) l();
  };
  const subscribe = (fn: () => void) => {
    listeners.add(fn);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      cached = null;
      fn();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(fn);
      window.removeEventListener("storage", onStorage);
    };
  };
  return { read, write, subscribe };
}

/** localStorage key read by every odds display ("decimal" | "fractional"). */
export const ODDS_FORMAT_KEY = "wb24.oddsFormat";
const oddsPref = localPreference<OddsFormat>(ODDS_FORMAT_KEY, (raw) => (raw === "fractional" ? "fractional" : "decimal"));
const hideBalancePref = localPreference<"1" | "0">("wb24.hideBalance", (raw) => (raw === "1" ? "1" : "0"));

/** The player's odds format (Decimal / Fractional) and a setter that updates every subscriber. */
export function useOddsFormat(): [OddsFormat, (f: OddsFormat) => void] {
  const value = useSyncExternalStore(oddsPref.subscribe, oddsPref.read, () => "decimal" as OddsFormat);
  return [value, oddsPref.write];
}

/** Whether the balance is masked in the header and account menu (eye toggle). */
export function useHideBalance(): [boolean, (hidden: boolean) => void] {
  const value = useSyncExternalStore(hideBalancePref.subscribe, hideBalancePref.read, () => "0" as const);
  const set = useCallback((hidden: boolean) => hideBalancePref.write(hidden ? "1" : "0"), []);
  return [value === "1", set];
}

// ---------------------------------------------------------------- shell API

export interface ShellApi {
  /** Opens the login or sign-up sheet (desktop modal, mobile bottom sheet). */
  openAuth: (mode: "login" | "register") => void;
  /** Opens the account drawer (signed in) or the site menu (signed out). */
  openAccountMenu: () => void;
}

const ShellCtx = createContext<ShellApi | null>(null);
export const ShellProvider = ShellCtx.Provider;

export function useShell(): ShellApi {
  const v = useContext(ShellCtx);
  if (!v) throw new Error("useShell outside Layout");
  return v;
}
